import { getConfigTenant, isCorpTenant } from "@capone/common";
import clsx from "clsx";
import {
  GenericInfoPopup,
  Icon,
  IconName,
  paymentTextConstants,
  useDeviceTypes,
} from "halifax";
import React, { useContext, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router";
import {
  ErrorModalType,
  getSubtitleTrackingValue,
  MODAL_ALERT,
  MODAL_ALERT_CHOICE,
  ModalAlertProperties,
  ModalButtonType,
  ModalCategoryType,
  ModalScreens,
  TripCategory,
} from "redmond";
import { ClientContext } from "../../../../App";
import { config } from "../../../../api/config";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { PATH_BOOK, PATH_TRIPS } from "../../../../utils/urlPaths";
import { MobileFlightBookWorkflowStep } from "../../component";
import { CheckoutSteps, Progress } from "../../reducer";
import {
  CAUTION_ICON,
  CHOOSE_ANOTHER_FLIGHT,
  CONFIRM_AND_BOOK,
  CONTINUE,
  CONTINUE_WITH_OWN_SEAT,
  EDIT_PAYMENT_INFO,
  ERROR_ICON,
  GO_TO_MY_TRIPS,
  MODIFY_PAX,
  PRICE_DECREASE_ICON,
  PRICE_INCREASE_ICON,
  SEARCH_AGAIN,
  TRY_AGAIN,
  UNABLED_ICON,
  UPDATE_PAYMENT_INFO,
  UPDATE_TRAVELERS,
} from "../../reducer/selectors/textConstants";
import { AGENT_FEE } from "../index";
import { BookingErrorModalConnectorProps } from "./container";
import { getRedirectToShopQueryParams } from "./redirectToShopQueryParams";
import "./styles.scss";

export interface IBookingErrorModalProps
  extends BookingErrorModalConnectorProps,
    RouteComponentProps {
  checkoutStep?: MobileFlightBookWorkflowStep;
  setCheckoutStep?: (step: any) => void;
  setLocalTravelerIds?: (ids: string[]) => void;
  setLocalLapInfantIds?: (ids: string[]) => void;
}

export const BookingErrorModal = ({
  bookingProgressList,
  priceDifference,
  errorTitles,
  hasError,
  resetBookingErrors,
  history,
  acknowledgePriceDifference,
  populateFlightShopQueryParams,
  scheduleQuote,
  currentSession,
  payments,
  ancillaries,
  isMissingPassport,
  resetSelectedTrip,
  modalType,
  infantOnlyError,
  setInfantOnlyError,
  checkoutStep,
  setCheckoutStep,
  priceQuote,
  selectedLapInfantIds,
  selectedPaxIds,
  setUserSelectedPassengerIds,
  setUserSelectedLapInfantIds,
  setLocalLapInfantIds,
  setLocalTravelerIds,
  isFlightBookWithAncillariesActive,
  resetPaymentCardSelectedAccounts,
  viewedTripSummaryProperties,
  tripCategory,
}: IBookingErrorModalProps) => {
  const {
    primaryButtonText,
    secondaryButtonText,
    type: errorModalType,
    subtitle,
    mobileSubtitle,
  } = errorTitles;
  const { matchesMobile } = useDeviceTypes();
  const clientContext = useContext(ClientContext);
  const { isAgentPortal } = clientContext;
  const [isOpen, setIsOpen] = useState(false);
  const displayedSubtitle =
    matchesMobile && !!mobileSubtitle ? mobileSubtitle : subtitle;
  const [ranScheduleQuote, setRanScheduleQuote] = useState(false);
  const isCorporate = isCorpTenant(getConfigTenant());
  const isMulticity = tripCategory === TripCategory.MULTI_CITY;

  const getCheckoutStep = (
    progressList: Array<{
      status: Progress;
      name: string;
      type: CheckoutSteps | any;
    }>,
  ): string => {
    const progress = progressList.find(
      (p) => p.status === Progress.IN_PROGRESS,
    );
    return progress?.name || "flights_checkout";
  };

  const modalEventProperties: ModalAlertProperties = {
    type: modalType,
    primary_button: errorTitles.primaryButtonText!,
    secondary_button: SEARCH_AGAIN,
    screen: ModalScreens.FLIGHTS_CHECKOUT,
    category: ModalCategoryType.TROUBLE,
    modal_subtitle: getSubtitleTrackingValue(errorTitles),
    modal_title: errorTitles.title,
    agent_title: errorTitles.agentTitle!,
    agent_subtitle: errorTitles.agentSubtitle!,
    funnel: "flights",
    step: getCheckoutStep(bookingProgressList),
  };

  useEffect(() => {
    if (hasError)
      trackEvent({
        eventName: MODAL_ALERT,
        properties: {
          ...modalEventProperties,
        },
      });
  }, [hasError]);

  useEffect(() => {
    // if we have error during scheduleQuote (where it's not price quote only), we can handle error
    if (priceQuote && matchesMobile && ranScheduleQuote && setCheckoutStep) {
      hasError
        ? checkoutStep !== MobileFlightBookWorkflowStep.RewardsAndPayment &&
          setCheckoutStep(MobileFlightBookWorkflowStep.RewardsAndPayment)
        : checkoutStep !== MobileFlightBookWorkflowStep.Review &&
          setCheckoutStep(MobileFlightBookWorkflowStep.Review);
    }
  }, [priceQuote]);

  const scrollToTravelers = () => {
    const travelerSectionElement = document.querySelector(
      ".traveler-select-workflow-container",
    );

    const BANNER_HEIGHT =
      document
        .getElementById("b2bportal-banner-container")
        ?.getBoundingClientRect().height ?? 0;
    const HEADER_HEIGHT = matchesMobile
      ? 0
      : document.querySelector(".app-header")?.getBoundingClientRect().height ??
        0;
    const MARGIN = matchesMobile ? 18 : 20;

    const yOffset =
      window.pageYOffset - (BANNER_HEIGHT + HEADER_HEIGHT + MARGIN);

    const y =
      (travelerSectionElement?.getBoundingClientRect().top ?? 0) + yOffset;

    window.scrollTo({ top: y, behavior: "smooth" });
  };

  /**
   * @description Spirtitual successor to `handleClickButton` that de-couples
   * the callback from the button text or modal type
   * @param {string} actionKey A key to get the callback by. May or may not be the button text
   * @param {boolean} [isPrimary=false] Used for event tracking
   */
  const handleButtonClick = (actionKey: string, isPrimary = false) => {
    handleResetErrors();
    setIsOpen(false);
    trackEvent({
      eventName: MODAL_ALERT_CHOICE,
      properties: {
        ...modalEventProperties,
        button_choice: isPrimary
          ? ModalButtonType.PRIMARY
          : ModalButtonType.SECONDARY,
      },
    });

    switch (actionKey) {
      case SEARCH_AGAIN:
        resetSelectedTrip();
        redirectToShop();
        break;
      case CHOOSE_ANOTHER_FLIGHT:
        redirectToShop();
        break;
      default:
    }
  };

  /**
   * @description A function that returns an on-click callback that performs
   * different actions based on the text on the button.
   * @param {boolean} isPrimary Whether or not the primary button was clicked
   */
  const handleClickButton = (isPrimary: boolean) => () => {
    const buttonText = isPrimary ? primaryButtonText : secondaryButtonText;
    const choice = isPrimary
      ? ModalButtonType.PRIMARY
      : ModalButtonType.SECONDARY;

    // note: this switch statement controls closing modal / resetting book errors
    if (isCorpTenant(config.TENANT) && buttonText === UPDATE_TRAVELERS) {
      setIsOpen(false);
    } else {
      handleResetErrors();
    }

    if (priceDifference.hasDifference) {
      acknowledgePriceDifference();
    }

    trackEvent({
      eventName: MODAL_ALERT_CHOICE,
      properties: {
        ...modalEventProperties,
        button_choice: choice,
      },
    });

    switch (buttonText) {
      case GO_TO_MY_TRIPS: {
        history.push({
          pathname: PATH_TRIPS,
        });
        break;
      }
      case CONFIRM_AND_BOOK: {
        // if (selectedTrip.tripId) {
        //   paymentRequest && priceQuoteSuccessful
        //     ? schedulePayment(paymentRequest, isAgentPortal ? AGENT_FEE : 0)
        //     : scheduleQuote({agentFee: isAgentPortal ? AGENT_FEE : 0});
        // }
        break;
      }
      case EDIT_PAYMENT_INFO:
      case paymentTextConstants.ADD_NEW_PAYMENT_METHOD:
        handleResetErrors(true);
        break;

      case UPDATE_PAYMENT_INFO: {
        switch (errorModalType) {
          case ErrorModalType.FLIGHTS_PRICE_QUOTE_HAS_PRICE_DIFFERENCE:
            acknowledgePriceDifference();
            break;
          default:
            break;
        }
        break;
      }
      case TRY_AGAIN: {
        // TODO: determining the funtionality of a button by its button text value feels limiting,
        // because it can end up having multiple different CTAs sharing the same button text
        switch (errorModalType) {
          case ErrorModalType.NO_ADULT_PASSENGER:
            scheduleQuote({
              agentFee: isAgentPortal ? AGENT_FEE : 0,
              pollQuoteOnly: true,
            });
            break;
          default:
            if (currentSession && payments && ancillaries) {
              scheduleQuote({
                agentFee: isAgentPortal ? AGENT_FEE : 0,
              });
              if (matchesMobile && setCheckoutStep) {
                // setting ranScheduleQuote here to go into useEffect above
                setRanScheduleQuote(true);
              }
            }
            break;
        }
        break;
      }
      case UPDATE_TRAVELERS: {
        if (infantOnlyError) {
          setInfantOnlyError(false);
        }
        break;
      }
      case CONTINUE:
        switch (errorModalType) {
          case ErrorModalType.FLIGHT_INACTIVITY:
          case ErrorModalType.PROVIDER_ERROR:
            if (currentSession && payments && ancillaries) {
              scheduleQuote({
                agentFee: isAgentPortal ? AGENT_FEE : 0,
                pollQuoteOnly: true,
              });
            }
            if (matchesMobile && setCheckoutStep) {
              // setting this to contact info so when we do get price quote, it'll increment step on MobileFlightBookingWorkflow to be seats or rewards/payment
              setCheckoutStep(MobileFlightBookWorkflowStep.ContactInformation);
            }
            break;
          default:
            break;
        }
        break;
      case CHOOSE_ANOTHER_FLIGHT: {
        redirectToShop();
        break;
      }
      case MODIFY_PAX: {
        if (matchesMobile && setCheckoutStep) {
          setCheckoutStep(MobileFlightBookWorkflowStep.TravelerInformation);
        } else {
          scrollToTravelers();
        }
        break;
      }
      case CONTINUE_WITH_OWN_SEAT: {
        setUserSelectedPassengerIds({
          userSelectedPassengerIds: [
            ...selectedPaxIds,
            ...selectedLapInfantIds,
          ],
          multiTicketType: viewedTripSummaryProperties?.multi_ticket_type,
        });
        setUserSelectedLapInfantIds({
          userSelectedLapInfantIds: [],
        });
        setLocalLapInfantIds && setLocalLapInfantIds([]);
        setLocalTravelerIds &&
          setLocalTravelerIds([...selectedPaxIds, ...selectedLapInfantIds]);
        scheduleQuote({
          agentFee: isAgentPortal ? AGENT_FEE : 0,
          pollQuoteOnly: true,
        });
        break;
      }
      default:
        break;
    }

    switch (errorModalType) {
      case ErrorModalType.FLIGHTS_PRICE_QUOTE_HAS_NO_AVAILABILITY:
        if (isPrimary) {
          resetSelectedTrip();
          redirectToShop();
        }
        break;
      case ErrorModalType.FLIGHTS_PRICE_QUOTE_HAS_PRICE_DIFFERENCE:
        /*
          note: in the flight-book variant of https://app.launchdarkly.com/capital-one/test/features/c1-fintech-ancillary-marketplace/targeting, the price quote can be invalidated by changing any
          of the ancillary seleciton options, and therefore requiring the user to reselect payment method to accommodate a new price quote
        */
        if (isFlightBookWithAncillariesActive) {
          resetPaymentCardSelectedAccounts();
        }
        break;
      case ErrorModalType.FLIGHTS_PRICE_QUOTE_WITH_ADD_ON_HAS_PRICE_DIFFERENCE:
        if (!isPrimary) {
          redirectToShop();
        }
        break;
      default:
        break;
    }
  };

  const handleResetErrors = (isPostQuote?: boolean) => {
    setIsOpen(false);
    resetBookingErrors(isPostQuote);
  };

  const getButtons = () => {
    let buttons = [];

    // buttons intentionally saved in reverse
    if (secondaryButtonText) {
      buttons.push({
        buttonText: secondaryButtonText,
        onClick: handleClickButton(false),
        defaultStyle: "h4r-secondary",
      });
    }

    if (primaryButtonText) {
      buttons.push({
        buttonText: primaryButtonText,
        onClick: handleClickButton(true),
        defaultStyle: "h4r-primary",
      });
    }

    // fallback so that at least one button shows up that can close the modal
    if (!primaryButtonText && !secondaryButtonText) {
      buttons.push(
        {
          buttonText: SEARCH_AGAIN,
          defaultStyle: "h4r-secondary",
          onClick: () => handleButtonClick(SEARCH_AGAIN),
        },
        {
          buttonText: CHOOSE_ANOTHER_FLIGHT,
          defaultStyle: "h4r-primary",
          onClick: () => handleButtonClick(CHOOSE_ANOTHER_FLIGHT, true),
        },
      );
    }

    // note: this buttons reverse logic should be refactored by applying it to specific errorModalType.
    if (
      (priceDifference.hasDifference || errorTitles.primaryButtonText) &&
      errorModalType !==
        ErrorModalType.FLIGHTS_PRICE_QUOTE_WITH_ADD_ON_HAS_PRICE_DIFFERENCE
    ) {
      buttons = [...buttons].reverse();
    }

    return buttons;
  };

  const getIconImage = (type: string) => {
    const iconMap: Record<string, JSX.Element> = {
      [ERROR_ICON]: isCorpTenant(config.TENANT) ? (
        <Icon className="error-icon" name={IconName.Caution} />
      ) : (
        <Icon className="error-icon" name={IconName.ErrorState} />
      ),
      [UNABLED_ICON]: (
        <Icon className="error-icon" name={IconName.UnableToProcess} />
      ),
      [PRICE_DECREASE_ICON]: (
        <Icon className="error-icon" name={IconName.ModalPriceDrop} />
      ),
      [PRICE_INCREASE_ICON]: (
        <Icon className="error-icon" name={IconName.PriceIncrease} />
      ),
      [CAUTION_ICON]: <Icon className="error-icon" name={IconName.Caution} />,
    };

    return iconMap[type] || iconMap[ERROR_ICON];
  };

  const redirectToShop = () => {
    populateFlightShopQueryParams({
      history,
      prevPath: PATH_BOOK,
      useHistoryPush: true,
      forceQueryUpdate: true,
      newQueryParams: getRedirectToShopQueryParams(isMulticity),
    });
  };

  useEffect(() => {
    setIsOpen(hasError && !isMissingPassport);
  }, [hasError, isMissingPassport]);

  return (
    <GenericInfoPopup
      agentSubtitle={errorTitles.agentSubtitle}
      agentTitle={errorTitles.agentTitle}
      buttons={getButtons()}
      // TODO: not the best solution out there, but some customization is possbile through adding errorModalType as the className
      className={clsx("flights-booking-error-modal", errorModalType, {
        mobile: matchesMobile,
        noIconPadding: isCorporate && errorTitles.icon === ERROR_ICON,
      })}
      image={getIconImage(errorTitles.icon as string)}
      isMobile={matchesMobile}
      isAgent={isAgentPortal}
      open={isOpen}
      subtitle={displayedSubtitle}
      title={errorTitles.title}
      // note: mobile popovers in the checkout workflow have z-index: 1300
      zIndex={matchesMobile ? 1301 : 1300}
    />
  );
};
